import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { Star } from 'lucide-react';
import { Modal, Button, TextArea, toast } from '../../components/common';
import { vendorApi } from '../../services/api';
import './VendorRating.css';

export default function VendorRatingModal({ vendor, isOpen, onClose, onSuccess }) {
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [comment, setComment] = useState('');

    const mutation = useMutation({
        mutationFn: (data) => vendorApi.addRating(vendor?.id, data),
        onSuccess: () => {
            toast.success('Rating submitted successfully');
            onSuccess?.();
            handleClose();
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to submit rating');
        }
    });

    const handleClose = () => {
        setRating(0);
        setHoverRating(0);
        setComment('');
        onClose();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (rating === 0) {
            toast.error('Please select a rating');
            return;
        }
        mutation.mutate({
            rating,
            comment
        });
    };

    const displayRating = hoverRating || rating;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={`Rate Vendor: ${vendor?.name || ''}`}
            size="sm"
        >
            <form onSubmit={handleSubmit} className="rating-form">
                <div className="rating-stars">
                    <label>Your Rating</label>
                    <div className="stars-row">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <button
                                key={star}
                                type="button"
                                className={`star-btn ${star <= displayRating ? 'active' : ''}`}
                                onClick={() => setRating(star)}
                                onMouseEnter={() => setHoverRating(star)}
                                onMouseLeave={() => setHoverRating(0)}
                            >
                                <Star size={32} fill={star <= displayRating ? '#fbbf24' : 'none'} />
                            </button>
                        ))}
                    </div>
                    <span className="rating-text">
                        {displayRating === 0 && 'Click to rate'}
                        {displayRating === 1 && 'Poor'}
                        {displayRating === 2 && 'Fair'}
                        {displayRating === 3 && 'Good'}
                        {displayRating === 4 && 'Very Good'}
                        {displayRating === 5 && 'Excellent'}
                    </span>
                </div>

                <TextArea
                    label="Comments (Optional)"
                    placeholder="Share your experience with this vendor..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={4}
                />

                <div className="form-actions">
                    <Button variant="secondary" type="button" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button type="submit" loading={mutation.isPending}>
                        Submit Rating
                    </Button>
                </div>
            </form>
        </Modal>
    );
}

VendorRatingModal.propTypes = {
    vendor: PropTypes.object,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func
};
